import { Calendar } from "lucide-react";

const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  return {
    year,
    month,
    daysInMonth: lastDay.getDate(),
    startingDayOfWeek: firstDay.getDay(),
  };
};

const getStatusForDay = (hours = 0, minutes = 0) => {
  const total = hours * 60 + minutes;

  if (total === 0)
    return { label: "Absent", color: "bg-red-100 text-red-700 border-red-300" };

  if (total < 60)
    return { label: "Partial", color: "bg-yellow-100 text-yellow-700 border-yellow-300" };

  if (total >= 120)
    return { label: "Full", color: "bg-green-100 text-green-700 border-green-300" };

  return { label: "Present", color: "bg-blue-100 text-blue-700 border-blue-300" };
};

const StaffAttendanceCalendar = ({
  workingHours = [],
  currentMonth,
  setCurrentMonth,
}) => {
  const { daysInMonth, startingDayOfWeek, year, month } =
    getDaysInMonth(currentMonth);

  const today = new Date();

  const getWorkingHoursForDate = (dateStr) =>
    workingHours.find(
      (wh) =>
        new Date(wh.date).toDateString() ===
        new Date(dateStr).toDateString()
    );

  const days = [];

  // Empty slots before month start
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(<div key={`empty-${i}`} />);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = new Date(year, month, day).toISOString();
    const workData = getWorkingHoursForDate(dateStr);
    const isToday =
      day === today.getDate() &&
      month === today.getMonth() &&
      year === today.getFullYear();

    const status =
      workData && getStatusForDay(workData.hours, workData.minutes);

    days.push(
      <div
        key={day}
        className={`aspect-square rounded-lg border flex flex-col items-center justify-center p-1 sm:p-2 transition ${isToday
          ? "ring-2 ring-primaryColor bg-brandAccentColor/20"
          : "bg-white"
          }`}
      >
        <span className="text-[11px] sm:text-sm font-semibold text-gray-700">
          {day}
        </span>

        {workData ? (
          <>
            <span
              className={`mt-1 px-1 py-0.5 rounded border text-[7px] sm:text-[10px] font-semibold ${status.color}`}
            >
              {status.label}
            </span>
            <span className="text-[9px] sm:text-[10px] text-gray-600">
              {workData.hours}h {workData.minutes}m
            </span>
          </>
        ) : (
          <span className="text-[9px] sm:text-[10px] text-gray-400 mt-1">
            —
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border shadow-sm p-2 sm:p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() =>
            setCurrentMonth(
              new Date(currentMonth.setMonth(currentMonth.getMonth() - 1))
            )
          }
          className="px-3 py-1.5 text-xs sm:text-sm border rounded-lg"
        >
          Prev
        </button>

        <h3 className="text-sm sm:text-base font-semibold flex items-center gap-2">
          <Calendar size={16} />
          {monthNames[month]} {year}
        </h3>

        <button
          onClick={() =>
            setCurrentMonth(
              new Date(currentMonth.setMonth(currentMonth.getMonth() + 1))
            )
          }
          className="px-3 py-1.5 text-xs sm:text-sm border rounded-lg"
        >
          Next
        </button>
      </div>

      {/* Weekday Header */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
        {weekDays.map((day) => (
          <div
            key={day}
            className="text-center text-[10px] sm:text-xs font-semibold text-gray-500"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {days}
      </div>
    </div>
  );
};

export default StaffAttendanceCalendar;
